import React, { useState, useCallback } from 'react';
import { DatePicker, Select, Button, Table, Statistic, Row, Col, Card, Icon, Tag } from 'antd';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import numeral from 'numeral';
import api from 'api';
import ErrorMessage from 'components/ErrorMessage';

// Read-only "Expiring Contracts" report (procedures/contracts.js: previewExpiringContracts).
// Lists non-default contracts whose current (un-renewed) term ends within the selected window
// AND that are NOT going to renew -- auto-renew off, or a cancel date set. Contracts set to
// auto-renew (and not cancelled) are excluded; they live on the Upcoming Contract Renewals
// preview. Rows link to the contract page. Download CSV mirrors the other batch/preview screens
// (client-side blob from the RPC's csv string).
//
// Superuser-only for now, same gate as the RPC (RPC_previewExpiringContracts, granted to 0
// roles; superuser bypasses).

const { Option } = Select;

const columns = [
    {title: 'Contract #', dataIndex: 'contract_number', key: 'contract_number',
     render: (v) => <span style={{fontWeight: 600}}>{v}</span>},
    {title: 'Site', dataIndex: 'site', key: 'site',
     render: (v) => v || <span style={{color: '#bbb'}}>(No Site)</span>},
    {title: 'Bill-To', dataIndex: 'bill_to_customer', key: 'bill_to_customer',
     render: (v) => v || <span style={{color: '#bbb'}}>(No Customer)</span>},
    {title: 'Type', dataIndex: 'contract_type', key: 'contract_type'},
    {title: 'Amount', dataIndex: 'amount', key: 'amount', align: 'right',
     render: (v) => `$${numeral(v).format('0,0.00')}`},
    {title: 'Start', dataIndex: 'start_date', key: 'start_date',
     render: (v) => v ? moment(v).format('MMM DD, YYYY') : ''},
    {title: 'Expires', dataIndex: 'expires', key: 'expires',
     render: (v) => v ? moment(v).format('MMM DD, YYYY') : ''},
    {title: 'Cancel Date', dataIndex: 'cancel_date', key: 'cancel_date',
     render: (v) => v ? moment(v).format('MMM DD, YYYY') : <span style={{color: '#bbb'}}>—</span>},
    {title: 'Why', dataIndex: 'reason', key: 'reason', align: 'center',
     render: (v) => v === 'Cancelled'
        ? <Tag color="red">Cancelled</Tag>
        : <Tag color="orange">No Auto-Renew</Tag>},
    {title: 'Status', dataIndex: 'status', key: 'status', align: 'center',
     render: (v) => v === 'expired'
        ? <Tag color="red">Expired</Tag>
        : <Tag color="blue">Active</Tag>},
];

function downloadCsv(csv, asOf) {
    const blob = new Blob([csv], {type: 'text/csv;charset=utf-8;'});
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `expiring_contracts_${asOf}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
}

function ExpiringContracts(props) {
    const [runDate, setRunDate] = useState(moment());
    const [horizon, setHorizon] = useState(3);
    const [loading, setLoading] = useState(false);
    const [report, setReport] = useState(null);
    const [error, setError] = useState(null);

    const run = useCallback(async () => {
        if (!runDate) {
            setError('Pick an as-of date first.');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const response = await api.rpc('previewExpiringContracts', {
                as_of_date: runDate.format('YYYY-MM-DD'),
                horizon_months: horizon,
            });
            const envelope = (response && response.body) || {};
            if (response && response.success && envelope.result && !envelope.error) {
                setReport(envelope.result);
            } else {
                const msg = (envelope.error && envelope.error.message)
                    || (response && response.errors && response.errors[0] && response.errors[0].message)
                    || 'The report could not be generated.';
                setError(msg);
                setReport(null);
            }
        } catch (e) {
            setError(String(e && e.message ? e.message : e));
            setReport(null);
        } finally {
            setLoading(false);
        }
    }, [runDate, horizon]);

    const rows = (report && report.contracts) || [];

    return <div style={{margin: 'auto', maxWidth: '1150px', padding: '24px'}}>
        <h1 style={{fontFamily: 'Raleway'}}>Expiring Contracts</h1>
        <p style={{color: '#666', maxWidth: '820px'}}>
            Contracts whose current term ends within the selected window that are <b>not</b> set
            to renew &mdash; auto-renew is off, or a cancel date has been set. Contracts set to
            auto-renew appear on the Upcoming Contract Renewals preview instead.
        </p>

        <div style={{display: 'flex', alignItems: 'flex-end', gap: '12px', marginBottom: '24px', flexWrap: 'wrap'}}>
            <div>
                <div style={{fontSize: '12px', color: '#888', marginBottom: '4px'}}>As of Date</div>
                <DatePicker
                    value={runDate}
                    onChange={setRunDate}
                    allowClear={false}
                    format="MMM DD, YYYY"
                    style={{width: '180px'}} />
            </div>
            <div>
                <div style={{fontSize: '12px', color: '#888', marginBottom: '4px'}}>Within</div>
                <Select value={horizon} onChange={setHorizon} style={{width: '150px'}}>
                    <Option value={3}>3 months</Option>
                    <Option value={6}>6 months</Option>
                    <Option value={12}>12 months</Option>
                </Select>
            </div>
            <Button type="primary" icon="search" loading={loading} onClick={run}>
                Run Report
            </Button>
            {report && rows.length > 0 &&
                <Button icon="download"
                        onClick={() => downloadCsv(report.csv, report.as_of_date)}>
                    Download CSV
                </Button>}
        </div>

        {error && <ErrorMessage errors={error} />}

        {report && <div>
            <Row gutter={16} style={{marginBottom: '24px'}}>
                <Col span={24}><Card><Statistic title="Expiring Contracts" value={report.count} /></Card></Col>
            </Row>

            {rows.length === 0
                ? <Card style={{textAlign: 'center', color: '#888'}}>
                    <Icon type="check-circle" style={{fontSize: '32px', color: '#52c41a'}} />
                    <div style={{marginTop: '8px'}}>
                        No contracts expire within {report.horizon_months} month
                        {report.horizon_months === 1 ? '' : 's'} of{' '}
                        {moment(report.as_of_date).format('MMM DD, YYYY')}.
                    </div>
                  </Card>
                : <Table
                    columns={columns}
                    dataSource={rows}
                    rowKey={(r, i) => `${r.contract_number}_${i}`}
                    pagination={false}
                    size="middle"
                    onRow={(r) => ({
                        onClick: () => props.history.push(`/contracts/${r.contract_number}`),
                        style: {cursor: 'pointer'},
                    })}
                    rowClassName={(r, i) => i % 2 ? 'row-alt' : ''} />}
        </div>}
    </div>;
}

export default withRouter(ExpiringContracts);
